// ============================================================================
// ui/gate.js — the landing gate (2026-07-14 redesign, un-modal'd same day)
// ============================================================================
// The very first thing a fresh visitor sees. First pass made this a floating
// popup over a blank dark page — Ryan: "looks like crap... we already have a
// perfect canvas to use." Rebuilt to render INLINE inside the exact same frame
// the real designer uses (`.wd-frame` > `.wd-stage-region.ds-canvas` >
// `.wd-canvas-stage` — the identical classes preview.js builds later), so the
// "What are we designing?" content sits directly on the tool's own light stage
// instead of floating a card over a dark scrim. No modal machinery (no scrim,
// no focus trap, no Esc/backdrop dismiss) — it's just the page's first content,
// same as any other step.
//
//   const choice = await openGate(ctx, mount); // 'design' | 'resume'
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { createStepTabs } from './shell/step-tabs.js';
import { createSelectWeightsStep } from './select-weights-step.js';
import { createMobileKindPager } from './mobile-kind-pager.js';
import { isPhone } from './mobile-launch.js';
import { createProductDetailBar } from './product-detail-bar.js';
import { hasAnyCategorySelected, hasAnySelection } from './state.js';

// The gate runs in two beats on one screen (2026-07-15, Ryan's spec) — the
// heading, sub-heading and button text all move with it. Beat 2's copy is
// deliberately about narrowing ("which ones"), not restating the question.
// The gate leaving (2026-07-15, Ryan: the old instant `frame.remove()` was "an
// abrupt snap"). The content goes first and the stage dissolves under it, the two
// overlapping so it reads as ONE move — see playExit.
const EXIT_CONTENT_MS = 380;
const EXIT_DISSOLVE_DELAY = 180;
const EXIT_DISSOLVE_MS = 520;

/** Reduced motion: hand over instantly. Same call every other beat here makes. */
const prefersReduced = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const BEATS = [
  { title: 'What are you designing?', sub: 'Pick some weights to customize.', cta: 'Next' },
  { title: 'Which ones?', sub: 'Choose the exact weights to customize.', cta: 'Start designing' },
];

/* Stand-in for the Resume pill's thumbnail when the save has no logo in it — a text-only design, or
   one abandoned before the upload. A plate outline rather than a generic document icon: it says
   which tool the saved thing belongs to, which is the only job left once there is no mark to show. */
const RESUME_GLYPH = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" '
  + 'stroke-width="1.7" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.2"/></svg>';

/**
 * @param {object} ctx - minimal context; the gate runs before the full app ctx (status bar, 3D
 *   stage, etc.) exists. `{ state, stepLabels?, announce?, resume? }`.
 * @param {{logoUrl: string|null}|null} [ctx.resume] - present when a resumable autosave exists;
 *   puts the Resume pill on beat 1. Absent (the common case) and the gate is exactly as it was.
 * @param {HTMLElement} mount - element to append the gate into (the root).
 * @returns {Promise<'design'|'resume'>} 'resume' means: restore the autosave instead of using the
 *   picks made here. The caller owns the restore — this only reports the choice.
 */
export function openGate(ctx, mount) {
  return new Promise((resolve) => {
    // ONE button drives both beats (2026-07-15 — earlier it was two buttons,
    // "Design my weights" / "Show me what's possible"; the latter is parked, not
    // deleted — Ryan may want it back once the flow settles).
    //   beat 1: "Next"           — enabled once ≥1 CATEGORY card is picked
    //   beat 2: "Start designing" — enabled once ≥1 PRODUCT is picked
    // Beat 1 → 2 plays the collapse-and-reveal choreography and stays on this
    // screen; beat 2 → hands off to the designer, loaded with the picks.
    let beat = 0;
    // Which BEAT-2 is on screen: the desktop collapse, or the phone's swipe pager. Back has to
    // undo whichever actually ran — see onBack.
    let pagerActive = false;
    let pagerInst = null;
    // PRE-FETCH the 3D chunks while the buyer is still choosing categories (Ryan 2026-07-26 —
    // "begin loading the models" on the previous screen). reveal-3d and the texture pipeline are
    // the two heaviest chunks in the bundle, and fetching them here means the pager usually opens
    // with the import cost already paid. Deferred a beat so it competes with nothing during the
    // gate's own first paint, and failure is silent: the pager imports them again on demand.
    if (isPhone()) {
      setTimeout(() => {
        import('../reveal-3d.js').catch(() => {});
        import('../texture-pipeline.js').catch(() => {});
      }, 500);
    }
    let animating = false;

    // Each gate button carries the same two-span rig as the product pills and the
    // theme's blog CTA: a scaleX(0) fill that wipes in from the left on hover,
    // and the label in its own layer above it.
    // The label MUST be its own span — the CTA text changes between beats, and
    // setting textContent on the button itself would blow the fill span away.
    const nextLabel = h('span', { class: 'wd-gate-btn__t' }, BEATS[0].cta);
    const nextBtn = h(
      'button',
      { type: 'button', class: 'wd-btn wd-btn--primary wd-gate-btn', disabled: true, onClick: onNext },
      h('span', { class: 'wd-gate-btn__fill', 'aria-hidden': 'true' }),
      nextLabel,
    );
    // Bottom-LEFT once you're in beat 2 (its `wd-gate-back` margin-right:auto
    // claims the left slot). Built once and added/removed rather than toggled
    // hidden, so beat 1's action row is genuinely just Next.
    // Deliberately NOT `wd-btn--ghost` (2026-07-15): that's the app's secondary
    // variant for the DARK designer chrome, and on the gate's white panel it
    // rendered invisible. `.wd-gate-back` carries its own on-white styling.
    const backBtn = h(
      'button',
      { type: 'button', class: 'wd-btn wd-gate-btn wd-gate-back', onClick: onBack },
      h('span', { class: 'wd-gate-btn__fill', 'aria-hidden': 'true' }),
      h('span', { class: 'wd-gate-btn__t' }, 'Back'),
    );
    /* ── RESUME LIVES ON THIS SCREEN NOW, NOT IN A MODAL OVER THE WHOLE PAGE ─────────────────────
     * Ryan 2026-08-09: *"should we make the resume/start fresh pop-up self-contained to the
     * window/editor instead of it hijacking the entire page? … you're not trying to actually design
     * something but perhaps just going to the page for another reason."*
     *
     * ⭐ THE MODAL EXISTED BECAUSE IT FORCED A BINARY, and that is what makes this a deletion rather
     * than a relocation. Once Resume is a control ON the welcome screen, **"Start fresh" stops
     * needing a button at all** — this screen IS starting fresh. So one pill replaces three controls
     * (Resume / Start fresh / ✕) plus a full-viewport scrim, a body scroll-lock, a focus trap and an
     * Escape handler, all of which ran before the visitor had seen a single thing.
     *
     * ⭐ AND IT STOPS BEING A ONE-SHOT. The modal asked once, at first paint, and was gone. This sits
     * there for as long as they are on the gate — including after `Back` from beat 2.
     *
     * ⚠ THE THUMBNAIL IS THE BUYER'S OWN LOGO, NOT A RENDER OF THE PLATE, and that is deliberate on
     * two counts. At 30px a whole plate is an illegible grey disc, whereas a mark is recognised
     * instantly; and rendering the design would mean cloning state and running the engine — with a
     * multi-MB logo embedded — at first paint, which is the one moment that cannot afford it. The
     * URL is read straight out of the envelope main.js has already parsed (see `readResume`).
     */
    const resumeBtn = ctx.resume ? h(
      'button',
      {
        type: 'button', class: 'wd-btn wd-gate-btn wd-gate-resume',
        title: 'Pick up the design you were working on',
        onClick: onResume,
      },
      h('span', { class: 'wd-gate-btn__fill', 'aria-hidden': 'true' }),
      ctx.resume.logoUrl
        ? h('img', { class: 'wd-gate-resume__art', src: ctx.resume.logoUrl, alt: '', loading: 'lazy' })
        : h('span', { class: 'wd-gate-resume__art wd-gate-resume__art--none', 'aria-hidden': 'true', html: RESUME_GLYPH }),
      h('span', { class: 'wd-gate-btn__t' }, 'Resume design'),
    ) : null;
    // The product plate lives in the CENTRE of the action row (2026-07-15, Ryan:
    // "in the bottom center of the display... we use that available space down
    // there"). It's absolutely centred on the row rather than laid out between the
    // buttons, so Back and the CTA can't shove it off-centre as their labels
    // change between beats. Beat 2 only — there are no pills to describe on beat 1.
    const detailBar = createProductDetailBar(ctx);
    /* Beat 1's left slot, which is empty today — `Back` only exists on beat 2 and claims the same
       place. They are never on screen together (see `showBack` / `onBack`), which is why one
       `margin-right: auto` slot serves both and the CTA on the right never moves. */
    const actions = h('div', { class: 'wd-gate-actions' }, ...(resumeBtn ? [resumeBtn] : []), detailBar.el, nextBtn);

    const selectWeights = createSelectWeightsStep(ctx, {
      onChange: () => { syncButton(); detailBar.sync(); },
      onPeek: (categoryId, kind) => detailBar.peek(categoryId, kind),
    });

    // Same WINDOW the real designer builds later (main.js: `h('div',
    // {class:'wd-frame'}, stepTabs.el, preview.el)` where preview.el is
    // `.wd-stage-region.ds-canvas`) — sharing `.wd-frame` + `.ds-canvas` is what
    // makes this read as "the same window," identical width AND height, not a
    // different screen bolted on front.
    // Deliberately NOT `.wd-canvas-stage` (2026-07-15): that's the *studio floor*
    // for the 3D plate (charcoal bg + radial key-light glow), and the gate shows
    // no plate — borrowing it only to cancel its look is the overcomplication
    // Ryan called out. The gate is just a white panel filling the window.
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE 2026-08-02 — h2, NOT h1. The page this mounts into already
       has an <h1>: the storefront banner's "Custom 3D Weight Designer" (and before WD 212, the
       embed section's own heading — so this was a second h1 long before the banner existed, not a
       regression from it). "What are you designing?" is a UI prompt, not the page's topic, and two
       h1s make the accessibility outline read as two competing page titles.
       ⚠ Multiple h1s do NOT hurt rankings — Google supports them in HTML5, and the server-rendered
       HTML only ever had one since this is injected after mount. This is a semantics/a11y fix.
       Safe as a pure tag swap: every rule for it is class-based (`.wd-gate-title`, x3, none
       tag-qualified), so nothing moves visually. */
    const titleEl = h('h2', { class: 'wd-gate-title' }, BEATS[0].title);
    const subEl = h('p', { class: 'wd-gate-sub' }, BEATS[0].sub);

    // The plate the gate dissolves INTO on the way out — it is the designer's own
    // stage, built from the same two tokens `.wd-canvas-stage` uses. Present from
    // the start at opacity 0 (rather than injected on exit) so the browser has
    // already rasterized its gradient before the exit needs it; creating it at exit
    // time risks a first-paint hitch in the middle of the dissolve.
    const exitStage = h('div', { class: 'wd-gate-exit-stage', 'aria-hidden': 'true' });
    // Named so the phone's beat 2 can swap its contents for the swipe pager (see onNext).
    const body = h('div', { class: 'wd-gate-body' }, selectWeights.el);
    const inline = h('div', { class: 'wd-gate-inline' }, titleEl, subEl, body, actions);
    const stage = h('div', { class: 'wd-gate-stage' }, exitStage, inline);
    const canvas = h('div', { class: 'wd-stage-region ds-canvas wd-gate-canvas' }, stage);
    // GHOST step-tab strip (Ryan 2026-07-18: gate→designer must be seamless — no
    // scroll, no shift). The designer's frame stacks a step-tab row ABOVE its
    // canvas; without the same row here the gate's canvas sat that row's flow
    // height (~38px) higher, so "Start designing" visibly dropped the whole
    // window. A real (invisible) strip — not hardcoded padding — because the tab
    // height is content-derived (padding + the 24px number circle) and a px copy
    // would silently drift on the next tab restyle. Labels don't affect height;
    // ctx.stepLabels (main.js's STEP_LABELS) keeps them honest if inspected.
    const ghostTabs = createStepTabs({
      steps: ctx.stepLabels || ['1', '2', '3', '4'],
      onSelect: () => {},
    });
    ghostTabs.el.classList.add('wd-gate-ghost-tabs');
    const frame = h('div', { class: 'wd-frame wd-gate-frame' }, ghostTabs.el, canvas);

    function syncButton() {
      if (animating) return; // the button stays down for the whole choreography
      // Each beat gates on its OWN list: categories first, then the actual
      // products (`selectedProducts` — what main.js reads to seed the designer).
      nextBtn.disabled = beat === 0 ? !hasAnyCategorySelected(ctx.state) : !hasAnySelection(ctx.state);
    }

    async function onNext() {
      if (nextBtn.disabled || animating) return;
      if (beat === 0) {
        animating = true;
        nextBtn.disabled = true;
        // PHONE: hand beat 2 to the swipe pager instead of running the desktop collapse (Ryan
        // 2026-07-26 — "once you click on the card(s), they snap around a bit"). That snapping IS
        // the FLIP choreography, which pins and measures cards for a horizontal ROW and was never
        // designed for the 2×2 grid a phone shows. Rather than teach it a second layout, the phone
        // gets a surface built for the shape of its screen: one product per page, swiped.
        if (isPhone()) {
          const cats = (ctx.state.ui.selectedCategories || []).slice();
          /* ⭐ THE MORPH GOES FIRST, AND THE 3D WAITS FOR IT (Ryan 2026-08-10: *"on mobile, when you
             click 'Next' … there's too much trying to load at once, and the button animation becomes
             really laggy"*).
               This branch was doing the exact thing the DESKTOP branch was fixed for a day earlier —
             `showBack()` sat at the BOTTOM, so the morph started only after ten pager pages had been
             built and mounted, and then ran straight into the pager's first geometry build. That
             build is synchronous and owns the thread for ~1s (mobile-kind-pager's own measurements),
             while the morph's middle beat is a `width` tween — layout, so main-thread by definition
             and unable to survive a single blocked frame, let alone sixty.
               Two things had to be true and neither was: the morph must START before the heavy screen
             is assembled, and the heavy work must not begin until it has LANDED. So they are ordered
             rather than raced. Cost is ~450ms before the first model starts building, on a screen
             that already shows its loader and is fully swipeable throughout. */
          const morphDone = Promise.race([
            // The CTA's longer word waits for the pill to make room for it — see setCtaCopy.
            showBack({ atMidpoint: () => setCtaCopy(1) }),
            // ⛔ UNCONDITIONAL RELEASE. If anything ever cancels or re-entrancy-guards that
            // choreography away, the pager must still get its 3D — a hold nobody settles is a
            // beat 2 with no models on it at all.
            new Promise((r) => setTimeout(r, 900)),
          ]);
          // …and the same for the word itself: idempotent, so this is free when the midpoint fired.
          morphDone.then(() => setCtaCopy(1));
          const pager = createMobileKindPager(ctx, cats, () => syncButton(), { holdUntil: morphDone });
          pagerInst = pager;
          body.classList.add('wd-gate-body--pager');
          replaceChildren(body, [pager.el]);
          pagerActive = true;
          beat = 1;
          animating = false;
          setBeatCopy(1, { keepCta: true });
          syncButton();
          return;
        }
        /* ⭐ THE BUTTON MORPHS *WITH* THE CARDS, not after them (Ryan 2026-08-09: *"the morph … should
           begin happening as soon as 'Next' is clicked, not after the cards finish their animation.
           So the cards will be animating AS the button is too. Much cleaner that way."*).
             It ran after the `await` purely because that is where the rest of the beat-2 setup
           lives, and the two have nothing to do with each other: the pill's width does not depend on
           the collapse, and the collapse does not read the pill. Sequencing them made one gesture
           into two, with a beat of dead air in the corner while the cards moved. */
        showBack();
        await selectWeights.collapseToProducts();
        beat = 1;
        animating = false;
        setBeatCopy(1);
        // AFTER the choreography, not before: it arrives with the pills it
        // describes, rather than sitting there through the collapse labelling
        // nothing.
        detailBar.setVisible(true);
        syncButton(); // re-gates on product picks — starts disabled again
        return;
      }
      nextBtn.disabled = true; // no double-click through the handoff
      finish('design');
    }

    /** The saved design, taken up from the welcome screen. `finish` plays the same exit as
     *  "Start designing" — the hand-off should read identically whichever way you got there. */
    async function onResume() {
      if (animating) return;
      animating = true;
      nextBtn.disabled = true;
      await finish('resume');
    }

    async function onBack() {
      if (animating || beat !== 1) return;
      // `animating` is the interruption guard, and it's load-bearing: onNext checks
      // it too, so hitting Next mid-reverse is ignored rather than starting a
      // forward run that fights the reverse for the same boxes.
      animating = true;
      nextBtn.disabled = true;
      morphBackToResume();   // Back fades, the box opens, Resume fades in — see below
      setBeatCopy(0);     // copy flips up front so the screen agrees with the motion
      // Goes with Back, up front and for the same reason: the pills it describes
      // are on their way out, and the product picks are cleared by the reverse, so
      // anything it kept showing would be describing something that's gone.
      detailBar.setVisible(false);
      // PHONE: there was no collapse to reverse. `resetToCategories()` unwinds the desktop FLIP
      // against `selectWeights.el` — which the pager swapped out of the DOM — so calling it here
      // threw on a detached node, and because that happens AFTER `backBtn.remove()` the button
      // vanished and `beat` never got back to 0 (Ryan 2026-07-26: "the button disappears and
      // doesn't actually take you back"). Put the grid back instead.
      if (pagerActive) {
        // Tear the WebGL context down BEFORE the DOM goes: a live context behind a dead screen is
        // the leak `dispose` exists for, and phones are the least forgiving place to hold one.
        try { pagerInst?.destroy?.(); } catch { /* already gone */ }
        pagerInst = null;
        body.classList.remove('wd-gate-body--pager');
        replaceChildren(body, [selectWeights.el]);
        pagerActive = false;
        // Match what the desktop reverse does: the KIND picks go, the CATEGORY picks stay — beat 0
        // is the category screen, and it should still show what was chosen there.
        ctx.state.ui.selectedProducts = [];
      } else {
        await selectWeights.resetToCategories();
      }
      beat = 0;
      animating = false;
      /* Resume comes back with beat 0, into the slot Back has just vacated. This is the half that
         makes it not a one-shot: the old modal asked once at first paint and was gone forever, so a
         buyer who dismissed it and then walked forward and back had no route to their own work. */
      syncButton(); // back to gating on category picks
    }

    /** The width half of both morphs, and the ONE place that knows an animated width is not a
     * rendered width.
     *
     * ⛔ AN EXPLICIT WIDTH ON A `flex-grow: 1` ITEM IS A REQUEST, NOT AN OUTCOME. The phone gate
     * gives `.wd-gate-btn` `flex: 1 1 auto` (styles §FULL-SCREEN GATE, "BIGGER BUTTONS") so the pill
     * and the CTA share the row — and free space is distributed AFTER the base size is read. So a
     * pill animating `width: w` renders at `w + (row − gap − w − CTA_natural) / 2`: it moves at HALF
     * the rate asked for, starts half a step from where it visually was, and at the final keyframe
     * sits a quarter of the free space PAST its resting width. The animation then clears, flex
     * re-measures, and it snaps back. Ryan 2026-08-10: *"it always overshoots the mark so the button
     * grows too big, then shrinks back down to size… the 'Next' button next to it shrinks a little
     * as it overshoots, then expands."*
     *
     * 🔑 ONE BUTTON WAS EXEMPT, WHICH IS WHY ONLY ONE DIRECTION SHOWED IT. `.wd-gate-back` is pinned
     * `flex: 0 0 auto` on the phone, so Resume→Back was always exact; `.wd-gate-resume` carries only
     * `.wd-gate-btn`, so it grows. Same bug in both directions on paper, visible in one.
     *
     * The fix is to make the tweened box behave like Back for the duration: pin it out of the
     * distribution, let the CTA absorb the free space (it already has `flex-grow: 1`), and the width
     * we animate is the width on screen — continuous at the start, exact at the end, monotonic
     * throughout. Restored on landing, where the pinned final value and the flex resting value are
     * the same number, so releasing it moves nothing.
     *
     * ⚠ INLINE, NOT A CLASS. The rule to beat is `#weight-designer-root.wd-fs .wd-gate-btn`, which
     * carries an ID — a `.wd-gate-btn.is-…` class loses to it silently and the overshoot would come
     * back looking like the fix never shipped.
     * ⚠ `toW` must be measured BEFORE the pin — that measurement IS the flex resting width.
     *
     * ⛔ THE LANDING IS TWO MUTATIONS AND THEY MUST SHARE ONE TASK. Dropping the width and releasing
     * the pin are each individually wrong: width-gone-while-pinned renders the pill at its bare
     * content width (32px narrow, measured), and pin-gone-while-width-held lets flex grow on top of
     * the final value — the very overshoot this exists to remove. So the tween is `fill: 'forwards'`
     * and BOTH are undone synchronously below, where no frame can be painted between them. A plain
     * `fill: 'none'` leaves the order to whether the engine's effect removal beats our `finished`
     * microtask, which is not a thing to be right by luck.
     */
    function tweenWidth(el, fromW, toW, duration, easing) {
      const prevFlex = el.style.flex;
      el.style.flex = '0 0 auto';
      const anim = el.animate(
        [{ width: `${Math.round(fromW)}px` }, { width: `${Math.round(toW)}px` }],
        { duration, easing, fill: 'forwards' },
      );
      // Cancelled counts as landed — see the callers. Release either way, or a cancelled morph
      // strands the pill outside the row's sizing (and holding a stale width) for the session.
      const release = () => {
        try { anim.cancel(); } catch { /* already idle */ }
        el.style.flex = prevFlex;
      };
      anim.finished.then(release, release);
      return anim;
    }

    /** Resume morphs into Back once the cards have settled (Ryan 2026-08-09).
     *
     * ⚠ ONE SLOT, TWO OCCUPANTS — that has always been true here, and it is why the plain swap read
     * as a jump cut: two pills of different widths trading places in the same spot, each with its
     * own entrance. Treating them as one object that CHANGES is the fix, and it costs one width
     * tween. The mirror of this is `morphBackToResume`; keep the two in step.
     *
     * ⛔ THE CONTENTS LEAVE BEFORE THE BOX CLOSES. Reversed, the pill squeezes with the words still
     * in it and they wrap — the same fault Ryan caught on the way back.
     * ⚠ Back's width is measured only AFTER it is in the DOM: it depends on its own font and
     * padding, so the FLIP order is measure-out, swap, measure-in, animate between.
     *
     * @param {{atMidpoint?: () => void}} [opts] fired on the frame Back's own word arrives — i.e.
     *   once the box has finished the half of its travel that matters. Anything else on the row that
     *   has to grow (the CTA's longer label) belongs there and nowhere earlier; see setCtaCopy.
     * @returns {Promise<void>} settles when the WIDTH tween lands — the one step here that is
     *   layout, and therefore the one the phone's beat 2 must not build 3D across (see onNext).
     *   The trailing label fade is opacity only, runs on the compositor, and is not waited for.
     */
    function showBack(opts = {}) {
      const from = resumeBtn && resumeBtn.isConnected ? resumeBtn.getBoundingClientRect() : null;
      const reduced = (() => {
        try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
      })();

      if (!from || reduced) {
        // No Resume on screen (or motion is unwelcome) — nothing to morph FROM.
        if (resumeBtn) resumeBtn.remove();
        actions.prepend(backBtn);
        // No box to time against, so whatever was waiting for the midpoint runs now — never never.
        opts.atMidpoint?.();
        if (reduced) return Promise.resolve();
        return backBtn.animate(
          [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
          { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        ).finished.catch(() => {});
      }

      const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
      const OUT = 150;
      const MORPH = 300;

      const art = resumeBtn.querySelector('.wd-gate-resume__art');
      const label = resumeBtn.querySelector('.wd-gate-btn__t');
      /* ⛔ HELD BY `fill: 'forwards'`, AND CANCELLED BY `morphBackToResume`. The fade has to persist
         while the pill shrinks, or the contents snap back mid-morph — but a forwards fill outlives
         the element, and Resume is detached and re-attached on the way back, where it returned the
         right size with no logo and no words until those animations were ended explicitly. */
      resumeBtn._wdMorphFades = [art, label].filter(Boolean).map((n) => n.animate(
        [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.86)' }],
        { duration: OUT, easing: 'ease-in', fill: 'forwards' },
      ));

      return new Promise((settle) => {
        setTimeout(() => {
          resumeBtn.remove();
          actions.prepend(backBtn);
          const to = backBtn.getBoundingClientRect();
          const t = backBtn.querySelector('.wd-gate-btn__t');
          // The word is held out while the box closes, then arrives — the mirror of the way back.
          const hold = t ? t.animate([{ opacity: 0 }, { opacity: 0 }], { duration: MORPH * 0.5, fill: 'forwards' }) : null;
          /* ⛔ A TRANSIENT WIDTH MUST NEVER BE ABLE TO RE-LINE THE ROW. The label timing above is
             what stops the overflow from happening; this is what stops an overflow from being
             catastrophic if some future label, font or narrower phone re-creates one. `nowrap` for
             the duration of the tween only — degraded then means a briefly tight button, not a row
             that jumps 56px and back. Deliberately NOT permanent: at rest the wrap is the correct
             graceful fallback on a genuinely narrow screen, where two lines beat two squashed pills. */
          actions.classList.add('is-morphing');
          const grow = tweenWidth(backBtn, from.width, to.width, MORPH, EASE);
          // Cancelled counts as landed: whoever is waiting is waiting to STOP WAITING, and a
          // rejected `finished` that nobody settled would hold them forever — and would strand the
          // row in `nowrap`.
          const done = () => { actions.classList.remove('is-morphing'); settle(); };
          grow.finished.then(done, done);
          setTimeout(() => {
            if (hold) { try { hold.cancel(); } catch { /* gone */ } }
            if (t) t.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
            // The box has done the half of its travel that frees the room. Everything that needed
            // that room arrives now, on this frame, together.
            opts.atMidpoint?.();
          }, MORPH * 0.5);
        }, OUT);
      });
    }

    /** Back → Resume, as the exact mirror of the way out (Ryan 2026-08-09).
     *
     * ⛔ THREE BEATS IN THIS ORDER, and the previous version had none of them: *"clicking the back
     * button should first fade the word 'Back', then expand the button to its longer length, then
     * have the logo and 'Resume design' fade in… you have it doing something different which has
     * text wrapping and it looks clunky."*
     *   1. "Back" fades OUT while its button is still on screen. It used to be removed outright, so
     *      there was nothing to fade and the swap read as a cut.
     *   2. The box opens from Back's width to Resume's, EMPTY. Resume's contents are pinned at zero
     *      opacity for this beat — the wrapping Ryan saw was "Resume design" sitting at full length
     *      inside a box still only as wide as "Back", so it broke onto a second line and shoved the
     *      pill's height around mid-tween.
     *   3. Only then do the logo and wording fade in.
     *
     * ⚠ The width is measured BEFORE Back leaves the DOM — a detached element measures zero.
     * ⚠ And the forward direction's `fill: 'forwards'` fades are cancelled on the way through, which
     * is the bug that made Resume come back empty at the right size.
     */
    function morphBackToResume() {
      const OUT = 150;
      const MORPH = 300;
      const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
      const fromWidth = backBtn.getBoundingClientRect().width;
      const backLabel = backBtn.querySelector('.wd-gate-btn__t');
      const reduced = (() => {
        try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
      })();

      const land = () => {
        backBtn.remove();
        if (!resumeBtn) return;
        actions.prepend(resumeBtn);
        (resumeBtn._wdMorphFades || []).forEach((a) => { try { a.cancel(); } catch { /* gone */ } });
        resumeBtn._wdMorphFades = null;
        const art = resumeBtn.querySelector('.wd-gate-resume__art');
        const label = resumeBtn.querySelector('.wd-gate-btn__t');
        const kids = [art, label].filter(Boolean);
        if (reduced) return;

        // Beat 2 — the box opens while the contents are held invisible, so nothing can wrap inside it.
        const hold = kids.map((n) => n.animate([{ opacity: 0 }, { opacity: 0 }], { duration: MORPH * 0.5, fill: 'forwards' }));
        const to = resumeBtn.getBoundingClientRect();   // opacity does not affect layout, so this is the real width
        /* ⛔ NO RE-LINING WHILE A PILL IS MID-TWEEN, the same guard the forward direction sets — and
           it belongs here more, not less: this direction is the one that GROWS, so the wide
           intermediate widths are all on this side of the morph. */
        actions.classList.add('is-morphing');
        const open = tweenWidth(resumeBtn, fromWidth, to.width, MORPH, EASE);
        const unwrap = () => actions.classList.remove('is-morphing');
        open.finished.then(unwrap, unwrap);
        // Beat 3 — and only once there is room for them.
        setTimeout(() => {
          hold.forEach((a) => { try { a.cancel(); } catch { /* gone */ } });
          kids.forEach((n) => n.animate(
            [{ opacity: 0, transform: 'scale(0.9)' }, { opacity: 1, transform: 'none' }],
            { duration: 200, easing: 'ease-out' },
          ));
        }, MORPH * 0.5);
      };

      if (reduced || !backLabel) { land(); return; }
      // Beat 1 — the word goes first, on the button that is still standing there.
      const fade = backLabel.animate([{ opacity: 1 }, { opacity: 0 }], { duration: OUT, easing: 'ease-in', fill: 'forwards' });
      setTimeout(() => {
        // Cleared so Back is not still invisible the next time it is used.
        try { fade.cancel(); } catch { /* gone */ }
        land();
      }, OUT);
    }

    /** The CTA's word, on its own, so the phone can land it LATER than the rest of the copy.
     *
     * ⭐ THE CTA'S BOX IS THE INVERSE OF THE PILL'S. They share one row at `flex: 1 1 auto`, so the
     * CTA's rendered width is simply `row − pill − gap`: as the pill shrinks 224→72 the CTA grows
     * 105→257 for free, and that part was always smooth. What was not smooth is that its LABEL
     * changed on frame 0, to a word that does not fit the space frame 0 has. Flexbox chooses line
     * breaks from HYPOTHETICAL sizes — before any shrinking — so a nowrap "Start designing"
     * (~190px min-content) beside a pill still 224px wide overflows a 339px row, and the row breaks
     * to two lines and back within ~50ms. Measured: 58 → 114 → 58px (tools/probe-gate-actions-wrap.mjs).
     * Ryan 2026-08-10: *"a really quick flash of the pill button in its longer form ABOVE the actual
     * pill button… this causes the button animating to lag for a split second too."*
     *
     * ⚠ THE DIRECTION DECIDES THE TIMING, and it is the same rule the pill already states for
     * itself: the row's budget is fixed, so the long label and the long pill must never want it at
     * once. Forward (pill shrinks) the word arrives LATE; back (pill grows) it must leave EARLY —
     * which `onBack` already does by calling `setBeatCopy(0)` up front, and is why only Next showed
     * this. Idempotent, so a backstop caller costs nothing. */
    function setCtaCopy(i) {
      if (nextLabel.textContent === BEATS[i].cta) return;
      nextLabel.textContent = BEATS[i].cta;
      if (prefersReduced()) return;
      // The same arrival as Back's own word, because they now land on the same frame.
      nextLabel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    }

    /** Swap the heading/sub/CTA with a short crossfade so the copy change reads
     *  as part of the same gesture rather than a jump-cut.
     *  @param {{keepCta?: boolean}} [opts] `keepCta` leaves the button's word alone — for the caller
     *    that is timing it against a width tween (see the note on setCtaCopy). */
    function setBeatCopy(i, opts = {}) {
      titleEl.textContent = BEATS[i].title;
      subEl.textContent = BEATS[i].sub;
      if (!opts.keepCta) nextLabel.textContent = BEATS[i].cta;
      ctx.announce?.(BEATS[i].title + ' ' + BEATS[i].sub);
      [titleEl, subEl].forEach((n) => n.animate(
        [{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }],
        { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      ));
    }

    /**
     * Beat 2 -> the designer. Ryan, 2026-07-15: "an abrupt snap, not a smooth
     * fade/slide/transition out" — because this used to be `frame.remove()`, the
     * gate gone in a single frame.
     *
     * Two overlapping moves, then the handoff:
     *   0-380ms   the content LEAVES — everything drifts up and fades. ease-IN
     *             (accelerating away), the mirror of the chrome's ease-out arrival:
     *             things that leave gather speed, things that arrive settle.
     *   180-700ms the stage DISSOLVES to the designer's own (see the CSS). Starts
     *             before the content is fully gone so it reads as one move rather
     *             than two queued ones — the same overlap the forward choreography
     *             uses.
     * Then the frame swap happens under an opaque plate that already IS the
     * designer's stage, so it can't be seen; main.js waits its 200ms and the chrome
     * arrives on the set the buyer has been looking at the whole time.
     *
     * The exit is awaited, so `openGate`'s promise doesn't resolve until the stage
     * is bare — main.js builds the designer AFTER, and its own reveal can't collide
     * with this one.
     * @returns {Promise<void>}
     */
    function playExit() {
      if (prefersReduced()) return Promise.resolve();
      const content = inline.animate(
        [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-14px)' }],
        { duration: EXIT_CONTENT_MS, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
      );
      const dissolve = exitStage.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: EXIT_DISSOLVE_MS, delay: EXIT_DISSOLVE_DELAY, easing: 'ease-in-out', fill: 'forwards' },
      );
      // Both, not just the longer one: `finished` rejects if an animation is
      // cancelled, and a half-dissolved gate handing off would show the seam.
      return Promise.all([content.finished, dissolve.finished]).catch(() => {});
    }

    async function finish(action) {
      await playExit();
      /* ⛔ THE PAGER'S WebGL CONTEXTS DIE HERE TOO, NOT ONLY ON `Back` (Ryan 2026-08-08: after
         "Start designing" the weight "will often just randomly disappear and all I can see is a
         white window… if I then close/minimize the app and reopen it, the weight will be back").
           `onBack` has torn the pager down since it was written, and its comment states the rule
         exactly — *"a live context behind a dead screen is the leak `dispose` exists for, and phones
         are the least forgiving place to hold one."* The FORWARD path never applied it. So on a
         phone the gate handed off still holding up to MAX_LIVE live contexts, the designer then
         built one more, and the phone evicted somebody — usually the newest, i.e. the designer's.
         That is the white window; `removing the frame from the DOM does not free a WebGL context.
           ⚠ AFTER `playExit`, not before. The Back path can destroy up front because it has no exit
         animation to protect; here the pager card is still visibly dissolving, and disposing it
         mid-animation would blank the model the buyer is watching leave. */
      try { pagerInst?.destroy?.(); } catch { /* already gone */ }
      pagerInst = null;
      pagerActive = false;
      frame.remove();
      resolve(action);
    }

    mount.append(frame);
  });
}
